import React from "react";
import { Box, Typography, Button } from "@mui/material";
import {
  CheckCircle,
  Download,
  ArrowBack as ArrowLeft,
} from "@mui/icons-material";
import { StepIndicator } from "./StepIndicator";
import * as XLSX from "xlsx";

export function ConfirmationScreen({ results, onBackToDashboard }) {
  const handleDownloadResults = () => {
    const resultData = [
      { Metric: "Total Records Processed", Count: results.total },
      { Metric: "Successfully Updated", Count: results.valid },
      { Metric: "Failed Updates", Count: results.invalid },
    ];

    const ws = XLSX.utils.json_to_sheet(resultData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Update Results");
    XLSX.writeFile(wb, "Bulk_Update_Results.xlsx");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F9FAFB",
      }}
    >
      <Box
        sx={{
          maxWidth: "896px",
          margin: "0 auto",
          padding: { xs: "32px 16px", sm: "32px 24px", lg: "32px 32px" },
        }}
      >
        <Box sx={{ mb: 4 }}>
          <StepIndicator currentStep={3} />
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#16A34A",
              padding: "32px 24px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                backgroundColor: "white",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <CheckCircle
                style={{ width: 40, height: 40, color: "#16A34A" }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: "white",
                mb: 1,
                fontWeight: 600,
              }}
            >
              Update Completed Successfully
            </Typography>
            <Typography sx={{ color: "#BBF7D0" }}>
              Your bulk update has been processed and applied to the student
              records.
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#111827",
                mb: 3,
                fontWeight: 600,
              }}
            >
              Update Summary
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Typography sx={{ color: "#374151", fontSize: "15px" }}>
                  Total Records Processed
                </Typography>
                <Typography
                  sx={{ color: "#111827", fontSize: "20px", fontWeight: 600 }}
                >
                  {results.total}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  backgroundColor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                }}
              >
                <Typography sx={{ color: "#15803D", fontSize: "15px" }}>
                  Successfully Updated
                </Typography>
                <Typography
                  sx={{ color: "#15803D", fontSize: "20px", fontWeight: 600 }}
                >
                  {results.valid}
                </Typography>
              </Box>

              {results.invalid > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    backgroundColor: "#FEF2F2",
                    border: "1px solid #FECACA",
                  }}
                >
                  <Typography sx={{ color: "#991B1B", fontSize: "15px" }}>
                    Failed Updates
                  </Typography>
                  <Typography
                    sx={{ color: "#991B1B", fontSize: "20px", fontWeight: 600 }}
                  >
                    {results.invalid}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
              }}
            >
              <Button
                onClick={handleDownloadResults}
                variant="outlined"
                startIcon={<Download style={{ width: 16, height: 16 }} />}
                sx={{
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                    borderColor: "#D1D5DB",
                  },
                }}
              >
                Download Result File
              </Button>

              <Button
                onClick={onBackToDashboard}
                variant="contained"
                startIcon={<ArrowLeft style={{ width: 16, height: 16 }} />}
                sx={{
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  backgroundColor: "#3B82F6",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                }}
              >
                Back to Bulk Edit
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#EFF6FF",
            border: "1px solid #BFDBFE",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ color: "#1E40AF", fontSize: "14px" }}>
            <Typography component="span" sx={{ fontWeight: 600 }}>
              Note:
            </Typography>{" "}
            All changes have been saved to the database. You can verify the
            updates by checking individual student records or running a report.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
